import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Loader2, Upload, CheckCircle2, XCircle, Copy, AlertCircle, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VALID_PROFILES = ['senior', 'salud', 'atletico', 'recomp', 'hipertrofia', 'none'];

/**
 * Parse one line in CSV-ish format:
 *   email,nombre,perfil,telefono
 *   email,nombre,perfil
 *   email,nombre
 * profile defaults to "none"; phone optional.
 */
function parseLine(raw) {
  const line = raw.trim();
  if (!line || line.startsWith('#')) return null;
  const parts = line.split(/[,;\t]/).map((p) => p.trim());
  const [email, name, profileRaw, phone] = parts;
  if (!email) return { error: 'Falta email', raw };
  if (!email.includes('@')) return { error: 'Email inválido', raw };
  const profile = (profileRaw || 'none').toLowerCase();
  const validProfile = VALID_PROFILES.includes(profile) ? profile : 'none';
  return {
    email: email.toLowerCase(),
    name: name || email.split('@')[0],
    profile: validProfile,
    phone: phone || null,
  };
}

/**
 * Diálogo de carga masiva de clientes.
 *
 * Props:
 *   - open: bool
 *   - onClose: () => void
 *   - onCreated: () => void (refresh padres)
 *   - role: 'admin' | 'trainer' (define el endpoint a usar)
 */
const BulkClientImportDialog = ({ open, onClose, onCreated, role = 'admin' }) => {
  const [text, setText] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  const parsed = useMemo(() => {
    return text
      .split('\n')
      .map(parseLine)
      .filter(Boolean);
  }, [text]);

  const validCount = parsed.filter((p) => !p.error).length;
  const errorCount = parsed.filter((p) => p.error).length;

  const handleSubmit = async () => {
    const validClients = parsed.filter((p) => !p.error);
    if (validClients.length === 0) {
      toast.error('Añade al menos una línea válida');
      return;
    }
    setSubmitting(true);
    try {
      const endpoint = role === 'trainer'
        ? `${API}/trainer/clients/bulk-create`
        : `${API}/admin/clients/bulk-create`;
      const res = await axios.post(endpoint, {
        clients: validClients,
        send_welcome_email: sendEmail,
      });
      setResults(res.data);
      toast.success(`${res.data.created} creados · ${res.data.skipped} omitidos · ${res.data.failed} fallidos`);
      if (res.data.created > 0 && onCreated) onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear clientes');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setText('');
    setResults(null);
    onClose();
  };

  const copyAllPasswords = () => {
    if (!results) return;
    const ok = results.results.filter((r) => r.ok);
    const text = ok.map((r) => `${r.email}\t${r.password}`).join('\n');
    navigator.clipboard.writeText(text);
    toast.success('Credenciales copiadas al portapapeles');
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-3xl bg-card border-border max-h-[90vh] overflow-y-auto"
        data-testid="bulk-import-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Carga masiva de clientes
          </DialogTitle>
        </DialogHeader>

        {!results && (
          <div className="space-y-4">
            <div className="bg-muted/40 border border-border rounded-md p-3 text-xs text-muted-foreground">
              <p className="font-semibold text-foreground mb-1">Formato (una línea por persona):</p>
              <code className="block bg-background/60 px-2 py-1 rounded text-[11px]">
                email, nombre, perfil, teléfono
              </code>
              <p className="mt-2">
                <strong>Perfil</strong> (opcional): <code>senior</code>, <code>salud</code>,{' '}
                <code>atletico</code>, <code>recomp</code>, <code>hipertrofia</code>,{' '}
                <code>none</code>. Si se reconoce, el cliente recibe el Macro automático.
              </p>
              <p className="mt-1">
                Si no escribes contraseña, se genera una segura y se te muestra al terminar.
              </p>
            </div>

            <div>
              <Label htmlFor="bulk-text">Lista de clientes</Label>
              <Textarea
                id="bulk-text"
                rows={10}
                placeholder={`maria@ejemplo.com, María García, senior, +34666111222
juan@ejemplo.com, Juan López, salud
pepe@ejemplo.com, Pepe Pérez`}
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="font-mono text-sm bg-background/60 border-border mt-1"
                data-testid="bulk-textarea"
              />
              <div className="flex items-center justify-between mt-2 text-xs">
                <div className="flex gap-3">
                  <span className="text-emerald-400">
                    <CheckCircle2 className="inline h-3 w-3 mr-1" />
                    {validCount} válidos
                  </span>
                  {errorCount > 0 && (
                    <span className="text-red-400">
                      <XCircle className="inline h-3 w-3 mr-1" />
                      {errorCount} con error
                    </span>
                  )}
                </div>
              </div>
            </div>

            {parsed.length > 0 && (
              <div className="border border-border rounded-md overflow-hidden max-h-48 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Email</TableHead>
                      <TableHead className="text-xs">Nombre</TableHead>
                      <TableHead className="text-xs">Perfil</TableHead>
                      <TableHead className="text-xs">Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsed.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs">{p.email || p.raw}</TableCell>
                        <TableCell className="text-xs">{p.name || '-'}</TableCell>
                        <TableCell className="text-xs">{p.profile || '-'}</TableCell>
                        <TableCell className="text-xs">
                          {p.error ? (
                            <span className="text-red-400">{p.error}</span>
                          ) : (
                            <span className="text-emerald-400">OK</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Checkbox
                id="bulk-send-email"
                checked={sendEmail}
                onCheckedChange={setSendEmail}
                data-testid="bulk-send-email-checkbox"
              />
              <Label htmlFor="bulk-send-email" className="text-sm cursor-pointer">
                Enviar email de bienvenida con credenciales
              </Label>
            </div>
          </div>
        )}

        {results && (
          <div className="space-y-4" data-testid="bulk-import-results">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-md p-3">
                <div className="text-2xl font-bold text-emerald-400">{results.created}</div>
                <div className="text-xs text-muted-foreground">creados</div>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-md p-3">
                <div className="text-2xl font-bold text-yellow-400">{results.skipped}</div>
                <div className="text-xs text-muted-foreground">omitidos</div>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-md p-3">
                <div className="text-2xl font-bold text-red-400">{results.failed}</div>
                <div className="text-xs text-muted-foreground">fallidos</div>
              </div>
            </div>

            {results.created > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={copyAllPasswords}
                className="w-full"
                data-testid="copy-passwords-btn"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copiar todas las credenciales (email + contraseña)
              </Button>
            )}

            <div className="border border-border rounded-md overflow-hidden max-h-72 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Email</TableHead>
                    <TableHead className="text-xs">Contraseña</TableHead>
                    <TableHead className="text-xs">Macro</TableHead>
                    <TableHead className="text-xs">Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.results.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs">{r.email}</TableCell>
                      <TableCell className="text-xs font-mono">
                        {r.password ? (
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(r.password);
                              toast.success('Copiado');
                            }}
                            className="hover:text-primary"
                            title="Click para copiar"
                          >
                            {r.password} <Copy className="inline h-3 w-3" />
                          </button>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-xs">{r.macro?.name || '-'}</TableCell>
                      <TableCell className="text-xs">
                        {r.ok ? (
                          <span className="text-emerald-400">creado</span>
                        ) : r.skipped ? (
                          <span className="text-yellow-400">{r.error}</span>
                        ) : (
                          <span className="text-red-400">{r.error}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-start gap-2 bg-blue-500/10 border border-blue-500/30 rounded-md p-3 text-xs">
              <AlertCircle className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-muted-foreground">
                Guarda las contraseñas: solo se muestran una vez. Los clientes pueden cambiarlas desde
                <strong> "¿Olvidaste tu contraseña?"</strong> en el login.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {!results ? (
            <>
              <Button variant="outline" onClick={handleClose} disabled={submitting}>
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || validCount === 0}
                data-testid="bulk-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creando...</>
                ) : (
                  <><Users className="h-4 w-4 mr-2" /> Crear {validCount} cliente{validCount !== 1 ? 's' : ''}</>
                )}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose} data-testid="bulk-close-btn">Cerrar</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkClientImportDialog;
